<script setup>
import { ref } from "vue";
import {
    useMiniCorporateEventsStore,
    register,
} from "@/stores/full/minicorporateevents/minicorporateevents.js";
import QPopover from "@/components/ui/QPopover.vue";
import { useI18n } from "vue-i18n";
import { storeToRefs } from "pinia";
import { qModEvent } from "@/utils/eventemitter";

const { t } = useI18n();

const props = defineProps({
    /**
     * Symbol to get the events for
     */
    symbol: {
        type: String,
        default: "MSFT",
    },

    /**
     * How many days plus/minus of the current day should we include
     */
    validRangeInDays: {
        type: String,
        default: "60",
    },

    /**
     * How many items to show in the popover
     */
    tooltipMaxCount: {
        type: Number,
        default: 3,
    },

    /**
     * Forward destination for the Earnings icon when `linkIcon` is true.
     */
    earningsForwardURL: {
        type: String,
        default: null,
    },

    /**
     * Forward destination for the Earnings Surprises icon when `linkIcon` is true.
     */
    surprisesForwardURL: {
        type: String,
        default: null,
    },

    /**
     * Forward destination for the Dividends icon when `linkIcon` is true.
     */
    dividendsForwardURL: {
        type: String,
        default: null,
    },

    /**
     * Forward destination for the Ratings icon when `linkIcon` is true.
     */
    ratingsForwardURL: {
        type: String,
        default: null,
    },

    /**
     * Forward destination for the Splits icon when `linkIcon` is true.
     */
    splitsForwardURL: {
        type: String,
        default: null,
    },

    /**
     * Target for forward navigation: icon links (when `linkIcon` is true)
     * @values _self, _blank
     */
    forwardURLTarget: {
        type: String,
        default: "_self",
    },

    /**
     * If true, the icons will be links to the respective forward URLs (if provided).
     * If false, the icons will not be links and only show the popover on hover/click.
     */
    linkIcon: {
        type: Boolean,
        default: false,
    },

    /**
     * Target for per-item "more information" links inside the popover content
     * (uses each item's own URL, e.g., `item.eventInfoURL`). Not used for icon links.
     * @values _self, _blank
     */
    eventInfoURLTarget: {
        type: String,
        default: "_self",
    },

    /**
     * Controls visibility of all "More" links inside popovers:
     * - Footer "More …" link (requires a forward URL and showMore)
     * - Per-item "more information" links (requires item.eventInfoURL)
     */
    showPopoverMoreLinks: {
        type: Boolean,
        default: true,
    },
});

const store = useMiniCorporateEventsStore();
const isPopoverShown = ref(false);
const popoverPosition = ref({});
const popover = ref({});
const { earnings, surprises, dividends, ratings, splits } = storeToRefs(store);

register();

store.init({
    symbol: props.symbol,
    t,
    validRange: parseInt(props.validRangeInDays, 10),
    tooltip: {
        max: parseInt(props.tooltipMaxCount, 10),
    },
});

await store.fetch();

let timeout = null;

const hidePopover = evt => {
    timeout = setTimeout(() => {
        isPopoverShown.value = false;
    }, 500);
};

const cancelHide = () => {
    clearTimeout(timeout);
};

const parseDataForCustomEvent = (title, data) => {
    const parsed = {
        title,
        content: data.items.map(item => {
            return {
                date: item.date,
                description: item.text,
                more: item.url,
            };
        }),
    };

    return parsed;
};

const togglePopover = (type, data, evt) => {
    cancelHide();

    const el = evt.target;
    const { bottom, left } = el.getBoundingClientRect();

    popoverPosition.value = { bottom, left };
    popover.value.title = `${type}`;
    popover.value.content = data;
    popover.value.moreLink = props[`${type}ForwardURL`];

    if (!isPopoverShown.value || popover.value.id !== type) {
        isPopoverShown.value = true;
    } else {
        isPopoverShown.value = false;
    }

    popover.value.id = type;

    qModEvent({
        tool: "MiniCorporateEvents",
        type: "showPopover",
        data: parseDataForCustomEvent(type, data),
    });
};
</script>

<template lang="pug">
.qmodii-ui-tool.qmodii-minicorporateevents
    .qmodii-tool-nodata(v-if="store.nodata") {{ t("labels.nodata") }}
    .qmodii-tool-container(v-else)
        a.qmodii-earnings.qmodii-event-icon(
            v-if="earnings.items.length",
            :href="props.linkIcon && props.earningsForwardURL ? props.earningsForwardURL : 'javascript:void(0)'",
            @mouseout="hidePopover",
            @mouseover="evt => togglePopover('earnings', earnings, evt)",
            @touchend="hidePopover",
            @touchstart="evt => togglePopover('earnings', earnings, evt)"
        )
            span.hidden Earnings
            svg(
                xmlns="http://www.w3.org/2000/svg",
                width="18",
                height="18",
                viewBox="0 0 18 18",
                fill="none"
            )
                path(
                    d="M8.31945 7.63889C8.31945 7.26458 8.6257 6.95833 9 6.95833C9.37431 6.95833 9.68056 7.26458 9.68056 7.63889V12.4028H8.31945V7.63889ZM14.4444 9.68056C14.0701 9.68056 13.7639 9.98681 13.7639 10.3611V13.7639H4.23611V4.23611H7.63889C8.0132 4.23611 8.31945 3.92986 8.31945 3.55556C8.31945 3.18125 8.0132 2.875 7.63889 2.875H4.23611C3.4875 2.875 2.875 3.4875 2.875 4.23611V13.7639C2.875 14.5125 3.4875 15.125 4.23611 15.125H13.7639C14.5125 15.125 15.125 14.5125 15.125 13.7639V10.3611C15.125 9.98681 14.8188 9.68056 14.4444 9.68056ZM15.125 4.23611H13.7639V2.875C13.7639 2.50069 13.4576 2.19444 13.0833 2.19444C12.709 2.19444 12.4028 2.50069 12.4028 2.875V4.23611H11.0417C10.6674 4.23611 10.3611 4.54236 10.3611 4.91667C10.3611 5.29097 10.6674 5.59722 11.0417 5.59722H12.4028V6.95833C12.4028 7.33264 12.709 7.63889 13.0833 7.63889C13.4576 7.63889 13.7639 7.33264 13.7639 6.95833V5.59722H15.125C15.4993 5.59722 15.8056 5.29097 15.8056 4.91667C15.8056 4.54236 15.4993 4.23611 15.125 4.23611ZM11.7222 9.68056C11.3479 9.68056 11.0417 9.98681 11.0417 10.3611V12.4028H12.4028V10.3611C12.4028 9.98681 12.0965 9.68056 11.7222 9.68056ZM5.59722 9V12.4028H6.95833V9C6.95833 8.6257 6.65208 8.31945 6.27778 8.31945C5.90347 8.31945 5.59722 8.6257 5.59722 9Z"
                ) 

        a.qmodii-dividends.qmodii-event-icon(
            v-if="dividends.items.length",
            :href="props.linkIcon && props.dividendsForwardURL ? props.dividendsForwardURL : 'javascript:void(0)'",
            @mouseout="hidePopover",
            @mouseover="evt => togglePopover('dividends', dividends, evt)",
            @touchend="hidePopover",
            @touchstart="evt => togglePopover('dividends', dividends, evt)"
        )
            span.hidden Dividends
            svg(
                xmlns="http://www.w3.org/2000/svg",
                width="18",
                height="18",
                viewBox="0 0 18 18",
                fill="none"
            )
                path(
                    d="M13.5 8.25V9.375C15.8925 9.375 17.3175 12.0375 15.9975 14.0325L15.18 13.215C15.795 11.97 14.8875 10.5 13.5 10.5V11.625L11.8125 9.9375L13.5 8.25ZM13.5 16.5V15.375C11.1075 15.375 9.6825 12.7125 11.0025 10.7175L11.82 11.535C11.205 12.78 12.1125 14.25 13.5 14.25V13.125L15.1875 14.8125L13.5 16.5ZM14.25 2.25H13.5V0.75H12V2.25H6V0.75H4.5V2.25H3.75C3.35218 2.25 2.97064 2.40804 2.68934 2.68934C2.40804 2.97064 2.25 3.35218 2.25 3.75V14.25C2.25 14.6478 2.40804 15.0294 2.68934 15.3107C2.97064 15.592 3.35218 15.75 3.75 15.75H10.5C10.0225 15.3358 9.63893 14.8244 9.375 14.25H3.75V6.75H14.25V7.9425C14.7825 8.025 15.2925 8.205 15.75 8.4825V3.75C15.75 3.35218 15.592 2.97064 15.3107 2.68934C15.0294 2.40804 14.6478 2.25 14.25 2.25ZM14.25 5.25H3.75V3.75H14.25"
                )
        a.qmodii-ratings.qmodii-event-icon(
            v-if="ratings.items.length",
            :href="props.linkIcon && props.ratingsForwardURL ? props.ratingsForwardURL : 'javascript:void(0)'",
            @mouseout="hidePopover",
            @mouseover="evt => togglePopover('ratings', ratings, evt)",
            @touchend="hidePopover",
            @touchstart="evt => togglePopover('ratings', ratings, evt)"
        )
            span.hidden Ratings
            svg(
                xmlns="http://www.w3.org/2000/svg",
                width="18",
                height="18",
                viewBox="0 0 18 18",
                fill="none"
            )
                path(
                    d="M13.506 2.24025C14.505 1.66875 15.75 2.39025 15.75 3.54225V12.9578C15.75 14.109 14.5057 14.8313 13.506 14.2598L9.5505 12H8.25V14.625C8.25 15.1223 8.05245 15.5992 7.70082 15.9508C7.34919 16.3025 6.87228 16.5 6.375 16.5C5.87772 16.5 5.4008 16.3025 5.04917 15.9508C4.69754 15.5992 4.5 15.1223 4.5 14.625V11.925C3.59148 11.7368 2.78493 11.2186 2.23613 10.4706C1.68732 9.72248 1.43519 8.79757 1.52843 7.87446C1.62167 6.95136 2.05367 6.09556 2.741 5.47234C3.42832 4.84912 4.3222 4.50271 5.25 4.5H9.5505L13.506 2.24025ZM9 6H5.25C4.65326 6 4.08096 6.23705 3.65901 6.65901C3.23705 7.08097 3 7.65326 3 8.25C3 8.84674 3.23705 9.41903 3.65901 9.84099C4.08096 10.2629 4.65326 10.5 5.25 10.5H9V6ZM10.5 10.815L14.25 12.9578V3.54225L10.5 5.685V10.815ZM6 12V14.625C6 14.7245 6.03951 14.8198 6.10983 14.8902C6.18016 14.9605 6.27554 15 6.375 15C6.47445 15 6.56984 14.9605 6.64016 14.8902C6.71049 14.8198 6.75 14.7245 6.75 14.625V12H6Z"
                )
        a.qmodii-surprises.qmodii-event-icon(
            v-if="surprises.items.length",
            :href="props.linkIcon && props.surprisesForwardURL ? props.surprisesForwardURL : 'javascript:void(0)'",
            @mouseout="hidePopover",
            @mouseover="evt => togglePopover('surprises', surprises, evt)",
            @touchend="hidePopover",
            @touchstart="evt => togglePopover('surprises', surprises, evt)"
        )
            span.hidden Surprises
            svg(
                xmlns="http://www.w3.org/2000/svg",
                width="18",
                height="18",
                viewBox="0 0 18 18",
                fill="none"
            )
                g(clip-path="url(#clip0_239_4741)")
                    path(
                        d="M16.084 7.03477C15.8365 6.77672 15.5813 6.51023 15.4976 6.30844C15.423 6.12844 15.4174 5.75789 15.4125 5.43164C15.402 4.72852 15.3886 3.85383 14.767 3.23297C14.1455 2.61211 13.2715 2.60016 12.5684 2.5875C12.2421 2.58258 11.8716 2.57695 11.6916 2.50242C11.4898 2.41875 11.2233 2.16352 10.9652 1.91602C10.4674 1.43859 9.84727 0.84375 9 0.84375C8.15273 0.84375 7.53258 1.43859 7.03477 1.91602C6.77672 2.16352 6.51023 2.41875 6.30844 2.50242C6.12844 2.57695 5.75789 2.58258 5.43164 2.5875C4.72852 2.60156 3.85383 2.61141 3.23438 3.23438C2.61492 3.85734 2.60156 4.72852 2.5875 5.43164C2.58258 5.75789 2.57695 6.12844 2.50242 6.30844C2.41875 6.51023 2.16352 6.77672 1.91602 7.03477C1.43859 7.53258 0.84375 8.15625 0.84375 9C0.84375 9.84375 1.43859 10.4674 1.91602 10.9688C2.16352 11.2268 2.41875 11.4933 2.50242 11.6951C2.57695 11.8751 2.58258 12.2456 2.5875 12.5719C2.59805 13.275 2.61141 14.1497 3.23297 14.7705C3.85453 15.3914 4.72852 15.4034 5.43164 15.416C5.75789 15.4209 6.12844 15.4266 6.30844 15.5011C6.51023 15.5848 6.77672 15.84 7.03477 16.0875C7.53258 16.5614 8.15625 17.1562 9 17.1562C9.84375 17.1562 10.4674 16.5614 10.9652 16.084C11.2233 15.8365 11.4898 15.5813 11.6916 15.4976C11.8716 15.423 12.2421 15.4174 12.5684 15.4125C13.2715 15.402 14.1462 15.3886 14.767 14.767C15.3879 14.1455 15.3998 13.2715 15.4125 12.5684C15.4174 12.2421 15.423 11.8716 15.4976 11.6916C15.5813 11.4898 15.8365 11.2233 16.084 10.9652C16.5614 10.4674 17.1562 9.84727 17.1562 9C17.1562 8.15273 16.5614 7.53258 16.084 7.03477ZM14.8662 9.79734C14.5273 10.1489 14.1434 10.5511 13.9388 11.0461C13.7405 11.5242 13.7327 12.0417 13.7278 12.543C13.7222 12.9199 13.7145 13.4367 13.5766 13.5738C13.4388 13.7109 12.9227 13.7194 12.5459 13.725C12.0445 13.7327 11.527 13.7405 11.0489 13.9359C10.5567 14.1405 10.1538 14.5245 9.80016 14.8634C9.54703 15.1059 9.16875 15.4688 9 15.4688C8.83125 15.4688 8.45297 15.1059 8.20266 14.8662C7.85109 14.5273 7.44891 14.1434 6.95391 13.9388C6.47578 13.7405 5.95828 13.7327 5.45695 13.7278C5.08008 13.7222 4.56328 13.7145 4.42617 13.5766C4.28906 13.4388 4.28062 12.9227 4.275 12.5459C4.26727 12.0445 4.25953 11.527 4.06406 11.0489C3.85945 10.5567 3.47555 10.1538 3.13664 9.80016C2.89406 9.54703 2.53125 9.16875 2.53125 9C2.53125 8.83125 2.89406 8.45297 3.13383 8.20266C3.47273 7.85109 3.85664 7.44891 4.06125 6.95391C4.25953 6.47578 4.26727 5.95828 4.27219 5.45695C4.28062 5.08008 4.28906 4.56328 4.42969 4.42969C4.57031 4.29609 5.08359 4.28414 5.46047 4.27852C5.9618 4.27078 6.4793 4.26305 6.95742 4.06758C7.44961 3.86297 7.8525 3.47906 8.20617 3.14016C8.45297 2.89406 8.83125 2.53125 9 2.53125C9.16875 2.53125 9.54703 2.89406 9.79734 3.13383C10.1489 3.47273 10.5511 3.85664 11.0461 4.06125C11.5242 4.25953 12.0417 4.26727 12.543 4.27219C12.9199 4.27781 13.4367 4.28555 13.5738 4.42336C13.7109 4.56117 13.7194 5.07727 13.725 5.45414C13.7327 5.95547 13.7405 6.47297 13.9359 6.95109C14.1405 7.44328 14.5245 7.84617 14.8634 8.19984C15.1031 8.45016 15.4659 8.82844 15.4659 8.99719C15.4659 9.16594 15.1059 9.54703 14.8662 9.79734ZM8.15625 9.28125V5.625C8.15625 5.40122 8.24514 5.18661 8.40338 5.02838C8.56161 4.87014 8.77622 4.78125 9 4.78125C9.22378 4.78125 9.43839 4.87014 9.59662 5.02838C9.75486 5.18661 9.84375 5.40122 9.84375 5.625V9.28125C9.84375 9.50503 9.75486 9.71964 9.59662 9.87787C9.43839 10.0361 9.22378 10.125 9 10.125C8.77622 10.125 8.56161 10.0361 8.40338 9.87787C8.24514 9.71964 8.15625 9.50503 8.15625 9.28125ZM10.125 12.0938C10.125 12.3163 10.059 12.5338 9.9354 12.7188C9.81179 12.9038 9.63609 13.048 9.43052 13.1331C9.22495 13.2183 8.99875 13.2405 8.78052 13.1971C8.56229 13.1537 8.36184 13.0466 8.2045 12.8892C8.04717 12.7319 7.94002 12.5315 7.89662 12.3132C7.85321 12.095 7.87549 11.8688 7.96064 11.6632C8.04578 11.4577 8.18998 11.282 8.37498 11.1583C8.55999 11.0347 8.7775 10.9688 9 10.9688C9.29837 10.9688 9.58452 11.0873 9.7955 11.2983C10.0065 11.5092 10.125 11.7954 10.125 12.0938Z"
                    )
        a.qmodii-splits.qmodii-event-icon(
            v-if="splits.items.length",
            @mouseout="hidePopover",
            @mouseover="evt => togglePopover('splits', splits, evt)",
            @touchend="hidePopover",
            @touchstart="evt => togglePopover('splits', splits, evt)",
            :href="props.linkIcon && props.splitsForwardURL ? props.splitsForwardURL : 'javascript:void(0)'"
        )
            span.hidden Splits
            svg(
                xmlns="http://www.w3.org/2000/svg",
                width="18",
                height="18",
                viewBox="0 0 18 18",
                fill="none"
            )
                path(
                    d="M3.75 9.75C3.5375 9.75 3.3595 9.678 3.216 9.534C3.0725 9.39 3.0005 9.212 3 9C2.9995 8.788 3.0715 8.61 3.216 8.466C3.3605 8.322 3.5385 8.25 3.75 8.25H8.7L12.45 4.5H11.25C11.0375 4.5 10.8595 4.428 10.716 4.284C10.5725 4.14 10.5005 3.962 10.5 3.75C10.4995 3.538 10.5715 3.36 10.716 3.216C10.8605 3.072 11.0385 3 11.25 3H14.25C14.4625 3 14.6408 3.072 14.7848 3.216C14.9288 3.36 15.0005 3.538 15 3.75V6.75C15 6.9625 14.928 7.14075 14.784 7.28475C14.64 7.42875 14.462 7.5005 14.25 7.5C14.038 7.4995 13.86 7.4275 13.716 7.284C13.572 7.1405 13.5 6.9625 13.5 6.75V5.55L9.73125 9.31875C9.59375 9.45625 9.4345 9.5625 9.2535 9.6375C9.0725 9.7125 8.88175 9.75 8.68125 9.75H3.75ZM11.25 15C11.0375 15 10.8595 14.928 10.716 14.784C10.5725 14.64 10.5005 14.462 10.5 14.25C10.4995 14.038 10.5715 13.86 10.716 13.716C10.8605 13.572 11.0385 13.5 11.25 13.5H12.45L10.5938 11.6625C10.4438 11.5125 10.3688 11.3345 10.3688 11.1285C10.3688 10.9225 10.4438 10.7443 10.5938 10.5938C10.7438 10.4433 10.925 10.3683 11.1375 10.3688C11.35 10.3693 11.5313 10.4443 11.6813 10.5938L13.5 12.45V11.25C13.5 11.0375 13.572 10.8595 13.716 10.716C13.86 10.5725 14.038 10.5005 14.25 10.5C14.462 10.4995 14.6403 10.5715 14.7848 10.716C14.9293 10.8605 15.001 11.0385 15 11.25V14.25C15 14.4625 14.928 14.6408 14.784 14.7847C14.64 14.9288 14.462 15.0005 14.25 15H11.25Z"
                )
    QPopover(
        @mouseover="cancelHide",
        @mouseout="hidePopover",
        :visible="isPopoverShown",
        @update:visible="isPopoverShown = $event",
        :position="popoverPosition",
        transition="fade",
        v-if="isPopoverShown"
    )
        h1 {{ t("corpevents." + popover.title.toLowerCase() + "events") }}
        ul
            li(v-for="item in popover.content?.items") 
                h3 {{ item.date }}
                h2
                    span.qmodii-event-description {{ item.text }}
                    a.qmodii-event-more(
                        v-if="props.showPopoverMoreLinks && item.eventInfoURL",
                        :href="item.eventInfoURL",
                        :target="props.eventInfoURLTarget"
                    ) {{ t("corpevents.moreinfo") }}
        footer(v-if="props.showPopoverMoreLinks && popover.content?.showMore && popover.moreLink")
            a(:href="popover.moreLink", :target="props.forwardURLTarget") {{ t("corpevents.more" + popover.id) }}
</template>

<style scoped>
.qmodii-minicorporateevents .qmodii-tool-container {
    display: flex;
    flex-direction: row;
    gap: 12px;
}

span.hidden {
    display: none;
}

.qmodii-event-icon {
    width: 28px;
    height: 28px;
    border-radius: 24px;
    background-color: #ccc;
    display: flex;
    justify-content: center;
    align-items: center;

    svg {
        pointer-events: none;

        path {
            fill: white;
            pointer-events: none;
        }
    }

    &.qmodii-earnings {
        background-color: #0477d126;

        svg path {
            fill: #0477d1;
        }
    }

    &.qmodii-dividends {
        background-color: #c6109a1a;

        svg path {
            fill: #c6109a;
        }
    }

    &.qmodii-surprises {
        background-color: #7691e21a;

        svg path {
            fill: #5174de;
        }
    }

    &.qmodii-splits {
        background-color: #0477d11a;

        svg path {
            fill: #0477d1;
        }
    }

    &.qmodii-ratings {
        background-color: #f7c34e26;

        svg path {
            fill: #f7c34e;
        }
    }
}

.qmodii-popover {
    margin-top: 12px;
    width: 290px;

    ul {
        list-style: none;
        margin: 0;
        padding: 8px;

        li {
            border-bottom: 1px solid #eee;
            padding-top: 8px;
            padding-bottom: 8px;

            &:first-child {
                padding-top: 0;
            }

            &:last-child {
                border-bottom: 0;
                padding-bottom: 0;
            }
        }
    }

    footer {
        padding: 8px;
        border-top: 1px solid #e3e3e3;
        line-height: 1;
        font-size: 14 px;

        a {
            color: #0d2c45;
            font-size: 14px;
            line-height: 1;
            text-decoration: none;
        }
    }

    h1,
    h2,
    h3 {
        color: #575757;
        font-size: 12px;
        line-height: 16px;
        text-transform: uppercase;
        margin: 0;
    }

    h1 {
        margin: 0;
        padding: 8px;
        color: #575757;
        font-size: 12px;
        font-style: normal;
        font-weight: 700;
        border-bottom: 1px solid #e3e3e3;
    }

    h2 {
        color: #222;
        font-size: 14px;
        line-height: 20px;
        font-weight: normal;
        text-transform: none;

        > a {
            color: #0d2c45;
        }
    }

    h3 {
        font-style: normal;
        font-weight: 400;
    }
}
</style>
